import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";

function CorrectionHistoryPanel({
  loading,
  corrections,
  formatCorrectionFieldLabel,
  formatCorrectionFromTo,
}) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (corrections.length === 0) {
    return (
      <Typography color="text.secondary">
        No correction history for this bond.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                backgroundColor: "#f0f0f0",
                color: "#111",
                fontWeight: 700,
                borderBottomColor: "rgba(0,0,0,0.12)",
              },
            }}
          >
            <TableCell>Date</TableCell>
            <TableCell>Field</TableCell>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Requester</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {corrections.map((row, idx) => (
            <TableRow key={row.id ?? `corr-${row.created_at}-${idx}`}>
              <TableCell>
                {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
              </TableCell>
              <TableCell>
                {formatCorrectionFieldLabel(row.changes?.field)}
              </TableCell>
              <TableCell>
                {formatCorrectionFromTo(row.changes?.field, row.changes?.from)}
              </TableCell>
              <TableCell>
                {formatCorrectionFromTo(row.changes?.field, row.changes?.to)}
              </TableCell>
              <TableCell>
                {row.requester_info?.name ?? "—"}{" "}
                {row.requester_info?.role ? `(${row.requester_info.role})` : ""}
              </TableCell>
              <TableCell>{row.status ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Data correction dialog shell with tabs, per-kind form content, and shared history table.
 */
export function BondRetrievalDataCorrectionDialog({
  open,
  onClose,
  dataCorrectionTab,
  setDataCorrectionTab,
  bondDataCorrectionsList,
  bondDataCorrectionsLoading,
  isSubmitting,
  onSubmit,
  formatCorrectionFieldLabel,
  formatCorrectionFromTo,
  renderCorrectDataTab,
}) {
  const handleClose = () => {
    onClose();
    setDataCorrectionTab(0);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Data Correction</DialogTitle>
      <Tabs
        value={dataCorrectionTab}
        onChange={(_, v) => setDataCorrectionTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
      >
        <Tab label="Correct data" />
        <Tab label="Correction history" />
      </Tabs>
      <DialogContent>
        {dataCorrectionTab === 0 && renderCorrectDataTab()}
        {dataCorrectionTab === 1 && (
          <Box sx={{ mt: 2 }}>
            <CorrectionHistoryPanel
              loading={bondDataCorrectionsLoading}
              corrections={bondDataCorrectionsList}
              formatCorrectionFieldLabel={formatCorrectionFieldLabel}
              formatCorrectionFromTo={formatCorrectionFromTo}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {dataCorrectionTab === 0 && (
          <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BondRetrievalDataCorrectionDialog;
